// Bayesian sandbox engine.
//
// Goal: an exec slides macro shock dials, sees readiness/relevance/timing
// posteriors update live with credible intervals, gets back the most
// information-bearing experiment to run.
//
// Design choice: closed-form Normal-Normal updates. No MCMC. The runtime is
// browser-only and we want sub-millisecond recomputation per slider tick.
// If we later need non-Normal likelihoods (count data on patents, etc.) we
// swap this out for a tiny Stan/PyMC-on-Pyodide call without changing the
// surface API.
//
// Math:
//   readiness ~ N(mu_r, sigma_r^2)
//   For each input i with score x_i ∈ {1,2,3} we treat it as a noisy
//   observation: x_i = readiness + alpha_i + shock_effect_i + epsilon_i,
//                epsilon_i ~ N(0, sigma_obs_i^2)
//   where shock_effect_i = sum_k beta_{i,k} * shock_k  (linear)
//
//   Posterior (independent normal factors → Bayesian-linear-regression
//   reduction): mu_post = (mu_prior/sigma_prior^2 + sum_i (x_i - delta_i)/sigma_obs_i^2)
//                          / (1/sigma_prior^2 + sum_i 1/sigma_obs_i^2)
//             precision_post = 1/sigma_prior^2 + sum_i 1/sigma_obs_i^2
//             sigma_post     = sqrt(1/precision_post)
//
// Where delta_i is the shock-driven shift on input i.
//
// "What experiment falsifies this?" → the input whose ABSORPTION (1/sigma_obs)
// would shrink the posterior variance the most given current observations.
// Equivalent to "expected information gain" with Normal-Normal updates.

import type { TopicWithContext } from "./types.ts";

export type ShockKey =
  | "regulatory_tightening"
  | "capital_availability"
  | "talent_supply"
  | "customer_readiness"
  | "competitive_intensity";

export const SHOCK_LABEL: Record<ShockKey, string> = {
  regulatory_tightening: "Regulatory tightening",
  capital_availability:  "Capital availability",
  talent_supply:         "Talent supply",
  customer_readiness:    "Customer readiness",
  competitive_intensity: "Competitive intensity",
};

export const SHOCK_HINT: Record<ShockKey, string> = {
  regulatory_tightening: "+1 = tighter rules; slows readiness on regulated topics",
  capital_availability:  "+1 = looser capital; speeds funding-dependent readiness",
  talent_supply:         "+1 = more talent; reduces execution risk",
  customer_readiness:    "+1 = customers are more ready; lifts relevance",
  competitive_intensity: "+1 = more competition; pulls timing in, lowers margins",
};

export const ALL_SHOCKS: ShockKey[] = [
  "regulatory_tightening", "capital_availability", "talent_supply",
  "customer_readiness", "competitive_intensity",
];

export type Shocks = Record<ShockKey, number>;

export const ZERO_SHOCKS: Shocks = {
  regulatory_tightening: 0,
  capital_availability:  0,
  talent_supply:         0,
  customer_readiness:    0,
  competitive_intensity: 0,
};

/**
 * Stipulated input → shock sensitivity matrix. β_{input, shock} = how much
 * a 1-unit shock moves the input's expected score (on the 1–3 scale).
 *
 * Tuned conservatively: |β| ≤ 0.5 so shocks shift things plausibly but not
 * absurdly. Curators will eventually edit these in Admin once a real
 * elicitation pass is done.
 */
const SENSITIVITY: Record<string, Partial<Record<ShockKey, number>>> = {
  // Readiness inputs
  research:      { talent_supply: +0.3, capital_availability: +0.1 },
  funding_yoy:   { capital_availability: +0.5, regulatory_tightening: -0.2 },
  capital:       { capital_availability: +0.5, competitive_intensity: +0.2 },
  institutional: { customer_readiness: +0.3, competitive_intensity: +0.2 },
  consumer:      { customer_readiness: +0.5 },
  regs:          { regulatory_tightening: +0.5 },                        // more policy activity tightens
  scale:         { customer_readiness: +0.4, capital_availability: +0.2 },
  timing:        { competitive_intensity: +0.4, capital_availability: +0.2, regulatory_tightening: -0.3 },
  // Lens-level inputs (for relevance posteriors)
  client_lens:     { customer_readiness: +0.4, regulatory_tightening: -0.1 },
  field_lens:      { capital_availability: +0.3, competitive_intensity: +0.2 },
  enterprise_lens: { regulatory_tightening: +0.3, talent_supply: +0.2 },
};

interface Observation {
  /** 1–3 observed score, or null if no observation. */
  score: number | null;
  /** Higher when an input is wired vs. AI-estimated; mapped to lower variance. */
  estimated: boolean;
  /** Sensitivity key into SENSITIVITY. */
  sensitivityKey: string;
}

export interface PosteriorPoint {
  /** Posterior mean. */
  mean: number;
  /** Posterior std-dev. */
  sd: number;
  /** 80% credible interval, low. */
  lo: number;
  /** 80% credible interval, high. */
  hi: number;
  /** Number of observations that informed the posterior. */
  n_obs: number;
}

export interface SandboxResult {
  readiness: PosteriorPoint;
  relevance: { client: PosteriorPoint; field: PosteriorPoint; enterprise: PosteriorPoint };
  timing_years: PosteriorPoint;
  /** Per-input expected information gain — drives "next experiment" suggestion. */
  experiments: Array<{
    inputKey: string;
    eig: number;
    rationale: string;
  }>;
  /** The single most-informative input to test next. */
  topExperiment: { inputKey: string; rationale: string } | null;
}

const PRIOR_MEAN = 2;     // 1–3 scale, neutral midpoint
const PRIOR_SD   = 1;     // wide
const SIGMA_WIRED     = 0.5;  // high precision
const SIGMA_ESTIMATED = 1.2;  // low precision

function sigmaOf(o: Observation): number {
  return o.estimated ? SIGMA_ESTIMATED : SIGMA_WIRED;
}

function shockShift(sensitivityKey: string, shocks: Shocks): number {
  const beta = SENSITIVITY[sensitivityKey] ?? {};
  let shift = 0;
  for (const k of ALL_SHOCKS) shift += (beta[k] ?? 0) * shocks[k];
  return shift;
}

function posteriorOf(observations: Observation[], shocks: Shocks): PosteriorPoint {
  const valid = observations.filter((o) => o.score != null);
  let precision = 1 / (PRIOR_SD * PRIOR_SD);
  let weighted = PRIOR_MEAN / (PRIOR_SD * PRIOR_SD);
  for (const o of valid) {
    const sigma = sigmaOf(o);
    const tau = 1 / (sigma * sigma);
    const shift = shockShift(o.sensitivityKey, shocks);
    weighted += (o.score! - shift) * tau;
    precision += tau;
  }
  const mean = weighted / precision;
  const sd = Math.sqrt(1 / precision);
  // 80% CI: ±1.2816σ
  return { mean, sd, lo: mean - 1.2816 * sd, hi: mean + 1.2816 * sd, n_obs: valid.length };
}

/** Expected information gain from adding one wired observation on this input. */
function expectedInformationGain(observations: Observation[], inputKey: string): number {
  // EIG for adding an obs with variance σ² to a Normal posterior with current
  // precision τ_post is ½ log(1 + τ_new/τ_post). Closed form, cheap.
  let precision = 1 / (PRIOR_SD * PRIOR_SD);
  for (const o of observations.filter((o) => o.score != null)) {
    precision += 1 / (sigmaOf(o) * sigmaOf(o));
  }
  const tauNew = 1 / (SIGMA_WIRED * SIGMA_WIRED);
  // If this input already has a wired observation, EIG is 0 — don't recommend duplicating.
  const existing = observations.find((o) => o.sensitivityKey === inputKey && o.score != null);
  if (existing && !existing.estimated) return 0;
  return 0.5 * Math.log(1 + tauNew / precision);
}

// ----------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------

export interface SandboxInputs {
  readinessInputs: Array<{ key: string; score: number | null; estimated: boolean }>;
  lensInputs: { client: number | null; field: number | null; enterprise: number | null };
  /** Topic's stipulated timing midpoint (years until significant impact). */
  timingMidpoint: number;
  shocks: Shocks;
  /** Optional weight overrides on the readiness inputs (1.0 default, 0–2 range). */
  weights?: Record<string, number>;
}

export function runSandbox(inputs: SandboxInputs): SandboxResult {
  // Readiness — N readiness inputs.
  const readinessObs: Observation[] = inputs.readinessInputs.map((r) => ({
    score: r.score,
    estimated: r.estimated,
    sensitivityKey: r.key,
  }));
  // Apply weight overrides by inflating/deflating sigma. weight=1 → unchanged;
  // weight=0 → infinite sigma (equivalent to dropping the obs).
  const weighted: Observation[] = readinessObs.map((o) => {
    const w = inputs.weights?.[o.sensitivityKey] ?? 1;
    if (w <= 0) return { ...o, score: null };
    if (w === 1) return o;
    // Effective σ scales as 1/√w.
    const baseSigma = sigmaOf(o);
    const effSigma = baseSigma / Math.sqrt(w);
    // Encode by toggling estimated based on relative sigma — simplest stable
    // hook into the existing sigmaOf path. For full fidelity we could carry
    // a custom sigma per obs, but that complicates the signature for v0.13.
    return { ...o, estimated: effSigma > (SIGMA_WIRED + SIGMA_ESTIMATED) / 2 };
  });

  const readiness = posteriorOf(weighted, inputs.shocks);

  // Relevance — one per lens. We treat the lens score as a single noisy obs.
  const lensObs = (lens: "client" | "field" | "enterprise"): Observation[] => {
    const score = inputs.lensInputs[lens];
    return [{
      score, estimated: false,
      sensitivityKey: `${lens}_lens`,
    }];
  };
  const relevance = {
    client:     posteriorOf(lensObs("client"),     inputs.shocks),
    field:      posteriorOf(lensObs("field"),      inputs.shocks),
    enterprise: posteriorOf(lensObs("enterprise"), inputs.shocks),
  };

  // Timing — pull-in by competitive_intensity, push-out by regulatory_tightening.
  // Treat the topic's stipulated midpoint as a prior, with the same noise model.
  const timingShift =
    -1.2 * inputs.shocks.competitive_intensity +
    +1.5 * inputs.shocks.regulatory_tightening +
    -0.7 * inputs.shocks.capital_availability;
  const timingMu = inputs.timingMidpoint + timingShift;
  const timingPoint: PosteriorPoint = {
    mean: timingMu,
    sd: 1.5,
    lo: timingMu - 1.2816 * 1.5,
    hi: timingMu + 1.2816 * 1.5,
    n_obs: 1,
  };

  // Experiments — EIG on each readiness input.
  const experiments = inputs.readinessInputs
    .map((r) => {
      const eig = expectedInformationGain(readinessObs, r.key);
      const rationale = r.score == null
        ? `No observation yet — wiring this would tighten the readiness posterior most.`
        : r.estimated
        ? `Currently AI-estimated. A wired source would replace high-variance evidence.`
        : `Already wired — adding another reading provides little new information.`;
      return { inputKey: r.key, eig, rationale };
    })
    .sort((a, b) => b.eig - a.eig);

  const topExperiment = experiments.length && experiments[0].eig > 0
    ? { inputKey: experiments[0].inputKey, rationale: experiments[0].rationale }
    : null;

  return { readiness, relevance, timing_years: timingPoint, experiments, topExperiment };
}

// ----------------------------------------------------------------------
// Convenience: build SandboxInputs from a TopicWithContext + the lens rollup.
// ----------------------------------------------------------------------

export function inputsFromTopic(
  ctx: TopicWithContext,
  lensScores: { client: number | null; field: number | null; enterprise: number | null },
  shocks: Shocks,
  weights?: Record<string, number>,
): SandboxInputs {
  // The order here matches the readiness input keys we expect on the metric
  // side (lib/memos.ts uses ctx.scores keyed by metric.key — same shape).
  const readinessInputs: SandboxInputs["readinessInputs"] = [
    "research", "funding_yoy", "capital", "institutional",
    "consumer", "regs", "scale", "timing",
  ].map((key) => {
    const s = ctx.scores[key];
    return {
      key,
      score: s?.score ?? null,
      estimated: s?.estimated ?? true,
    };
  });

  // Topic's stipulated timing midpoint from new schema columns; falls back to
  // 5 (mid of 0–10y) when not yet set.
  const tmin = ctx.topic.timing_years_min ?? null;
  const tmax = ctx.topic.timing_years_max ?? null;
  const timingMidpoint = (tmin != null && tmax != null) ? (tmin + tmax) / 2 : 5;

  return {
    readinessInputs,
    lensInputs: lensScores,
    timingMidpoint,
    shocks,
    weights,
  };
}

// ----------------------------------------------------------------------
// Saved scenarios — persistence (localStorage v1; Supabase later).
// ----------------------------------------------------------------------

export interface SavedScenario {
  id: string;
  name: string;
  thesis: string;
  topic_id: number;
  topic_name: string;
  shocks: Shocks;
  weights: Record<string, number>;
  saved_at: string;
}

const SCENARIOS_KEY = "tt:scenarios:v1";

export function listScenarios(): SavedScenario[] {
  try {
    const raw = localStorage.getItem(SCENARIOS_KEY);
    return raw ? (JSON.parse(raw) as SavedScenario[]) : [];
  } catch { return []; }
}

export function saveScenario(s: Omit<SavedScenario, "id" | "saved_at">): SavedScenario {
  const id = crypto.randomUUID?.() ?? `sc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const saved: SavedScenario = { ...s, id, saved_at: new Date().toISOString() };
  const list = listScenarios().concat(saved);
  localStorage.setItem(SCENARIOS_KEY, JSON.stringify(list));
  return saved;
}

export function deleteScenario(id: string): void {
  const list = listScenarios().filter((s) => s.id !== id);
  localStorage.setItem(SCENARIOS_KEY, JSON.stringify(list));
}
